import { defineComponent as _defineComponent } from 'vue'
import { resolveDirective as _resolveDirective, createElementVNode as _createElementVNode, withDirectives as _withDirectives, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import { RadioGroup } from '@components/Form/Radio';
import RichTranslation from '@shell/components/RichTranslation.vue';
import { RcButton } from '@components/RcButton';
import { RcModal } from '@components/RcModal';
import { downloadFile } from '@shell/utils/download';
import { escapeHtml } from '@shell/utils/string';
import { exportColumnsFor, rowsToCsv, rowsToJson } from '@shell/utils/table-views/export';
import { useI18n } from '@shell/composables/useI18n';
import { NotificationLevel } from '@shell/types/notifications';
import type { TableViewRow } from '@shell/types/table-views';

const SLOW_EXPORT_ROWS = 1000;

type Format = typeof FORMATS[number];

interface ExportResource extends TableViewRow {
  type: string;
  schema?: object;
  $ctx?: { getters?: { paginationEnabled?: (args: { id: string }) => boolean } };
  nameDisplay?: string;
  downloadYaml(): Promise<unknown>;
  downloadYamlBulk(items: ExportResource[], onProgress?: (done: number, total: number) => void): Promise<unknown>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TableViewExportModal',
  props: {
    modal: {},
    count: { default: 0 },
    viewName: { default: '' },
    isSelection: { type: Boolean, default: false },
    resources: { default: () => [] }
  },
  emits: ["export"],
  setup(__props: any, { emit: __emit }) {

/**
 * Asks what to export as. From the toolbar the table does the writing; from a resource action the
 * resources come in as a prop and are written here, followed in the notification centre
 */
const FORMATS = ['yaml', 'json', 'csv'] as const;

/** An "all matching" export fetches this many at a time, so past it there is more than one request */
const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

/** A resource action wants the resources as held (YAML); a view is exported as a spreadsheet */
const format = ref<Format>(props.isSelection ? 'yaml' : 'csv');

const formatOptions = computed(() => FORMATS.map((f) => ({ value: f, label: t(`tableViews.export.format.${ f }`) })));

/** The type's own columns: a resource action never learns which table it was picked in */
const selectionColumns = computed(() => {
  const first = props.resources[0];
  const schema = first?.schema;

  if (!schema) {
    return [];
  }

  // Paginated lists have their own column set, so ask for the one the table is showing
  const paginated = !!first.$ctx?.getters?.paginationEnabled?.({ id: first.type });
  const headers = store.getters['type-map/headersFor'](schema, paginated);

  return exportColumnsFor(headers, (key: string) => t(key));
});

const title = computed(() => (props.isSelection ? t('tableViews.export.selectionTitle') : t('tableViews.export.title')));

const intro = computed(() => {
  const slow = (props.count ?? 0) > SLOW_EXPORT_ROWS;

  if (props.isSelection) {
    return { k: slow ? 'tableViews.export.selectionIntroSlow' : 'tableViews.export.selectionIntro', args: { count: props.count } };
  }

  // The name is text the user typed, not markup
  const name = escapeHtml(props.viewName);

  if (props.count === null) {
    return { k: 'tableViews.export.introUncounted', args: { name } };
  }

  return { k: slow ? 'tableViews.export.introSlow' : 'tableViews.export.intro', args: { count: props.count, name } };
});

/** Writes the file and says what it was called. Given everything it needs, as the modal is closed by then */
const exportResources = async(items: ExportResource[], as: Format, columns: ReturnType<typeof exportColumnsFor>, onProgress: (done: number, total: number) => void) => {
  const first = items[0];

  if (as === 'yaml') {
    if (items.length === 1) {
      await first.downloadYaml();

      return `${ first.nameDisplay }.yaml`;
    }

    await first.downloadYamlBulk(items, onProgress);

    return 'resources.zip';
  }

  const name = (first?.type || 'resources').replace(/[^a-z0-9]+/gi, '-');

  if (as === 'json') {
    await downloadFile(`${ name }.json`, rowsToJson(items, columns), 'application/json;charset=utf-8');

    return `${ name }.json`;
  }

  await downloadFile(`${ name }.csv`, rowsToCsv(items, columns), 'text/csv;charset=utf-8');

  return `${ name }.csv`;
};

/** What the selection is, eg "Pods": items when its rows are of more than one type, as on Workloads */
const selectionType = (items: ExportResource[]): string => {
  const count = items.length;
  const { schema, type } = items[0] || {};

  if (schema && items.every((item) => item.type === type)) {
    return store.getters['type-map/labelFor'](schema, count);
  }

  return t('tableViews.export.notification.items', { count });
};

const exportSelection = async(items: ExportResource[], as: Format, columns: ReturnType<typeof exportColumnsFor>) => {
  const count = items.length;
  const type = selectionType(items);
  const id = await store.dispatch('notifications/add', {
    level:   NotificationLevel.Task,
    title:   t('tableViews.export.notification.title'),
    message: t('tableViews.export.notification.selectionMessage', {
      count, type, format: as.toUpperCase()
    }, true),
    progress: 0,
  });
  const onProgress = (done: number, total: number) => store.dispatch('notifications/update', { id, progress: Math.round((100 * done) / (total || 1)) });

  try {
    const file = await exportResources(items, as, columns, onProgress);

    await store.dispatch('notifications/update', {
      id,
      level:   NotificationLevel.Success,
      title:   t('tableViews.export.notification.doneTitle'),
      message: t('tableViews.export.notification.selectionDoneMessage', {
        count, type, file
      }, true),
      progress: 100,
    });
  } catch (e) {
    console.error('Unable to export the selection', e); // eslint-disable-line no-console

    await store.dispatch('notifications/update', {
      id,
      level:   NotificationLevel.Error,
      title:   t('tableViews.export.notification.failedTitle'),
      message: t('tableViews.export.notification.selectionFailedMessage', { count, type }, true),
    });
  }
};

const download = () => {
  if (props.resources.length) {
    // Read now: the modal, and its props, go with the close
    const items = [...props.resources];
    const as = format.value;
    const columns = selectionColumns.value;

    props.modal.onClose();
    // Running on with nothing on screen, so a failure the notification can't report is at least logged
    exportSelection(items, as, columns).catch((e) => console.error('Unable to export the selection', e)); // eslint-disable-line no-console

    return;
  }

  emit('export', format.value);
  props.modal.onClose();
};

return (_ctx: any,_cache: any) => {
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createBlock(_unref(RcModal), _mergeProps(__props.modal, {
    title: title.value,
    class: "export-modal",
    "data-testid": "table-views-export-modal"
  }), {
    "primary-action": _withCtx(() => [
      _createVNode(_unref(RcButton), {
        variant: "primary",
        size: "large",
        "left-icon": "download",
        "data-testid": "table-views-export-download",
        onClick: download
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('tableViews.export.download')), 1)
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(RichTranslation, {
        k: intro.value.k,
        args: intro.value.args,
        tag: "p",
        class: "export-intro"
      }, {
        b: _withCtx(({ content }) => [
          _withDirectives(_createElementVNode("b", null, null, 512), [
            [_directive_clean_html, content]
          ])
        ]),
        _: 1
      }, 8, ["k", "args"]),
      _cache[2] || (_cache[2] = _createTextVNode()),
      _createElementVNode("div", null, [
        _createVNode(RichTranslation, {
          k: "tableViews.export.choose",
          tag: "p",
          class: "export-choose"
        }, {
          b: _withCtx(({ content }) => [
            _withDirectives(_createElementVNode("b", null, null, 512), [
              [_directive_clean_html, content]
            ])
          ]),
          _: 1
        }),
        _cache[1] || (_cache[1] = _createTextVNode()),
        _createVNode(_unref(RadioGroup), {
          value: format.value,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((format).value = $event)),
          name: "table-views-export-format",
          options: formatOptions.value,
          row: true,
          "aria-label": title.value,
          "data-testid": "table-views-export-formats"
        }, null, 8, ["value", "options", "aria-label"])
      ]),
      _cache[3] || (_cache[3] = _createTextVNode())
    ]),
    _: 1
  }, 16, ["title"]))
}
}

})